import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { apiError } from "../../shared/api/client";
import { websiteApi } from "../../shared/api/website";
import { zaloMiniAppApi, zaloMiniAppQueryKeys } from "../../shared/api/zaloMiniApp";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Jump-off to Addy Sites, where the Mini App's content and theme actually live (ADR-0041 /
 * ADR-0016 Addendum A). Without this the page is a dead end: it tells the clinic that colours, logo
 * and block layout are read at app start rather than baked into the build, but nothing said WHERE to
 * change them — so the only way in was to know the CMS URL by heart.
 *
 * Reuses the /website SSO link as-is. That endpoint is tenant-scoped (`clinic.write` + an email on
 * the account) and does NOT require a website to exist, so a clinic that only has a Mini App still
 * gets in. The CMS is per-clinic, which is now the only shape there is — see MiniAppPage on why the
 * brand picker that used to sit under this card is gone.
 *
 * <p><b>Not linked yet? The button still opens.</b> Addy Sites only learns the clinic's Zalo app id
 * from MINIAPP_BINDING_CHANGED_V1, which is published when the binding below is saved — so before
 * that, Sites can let you edit content but has no app to publish to. Warn rather than block, for
 * two reasons. First, this SSO opens the CMS that ALSO serves the website: gating it on a Zalo
 * binding would mean a clinic with no Mini App could not edit its own website copy from here, and
 * it could open the very same editor from /website anyway — a lock with a door beside it. Second,
 * writing the content before wiring Zalo is a normal order to work in. What was actually missing
 * was being TOLD, which is what the notice does — and it now says who fixes it (Addy), not "go
 * type an id", because the clinic-facing form for that is gone: an Addy admin sets the binding at
 * HIS /platform/zalo.
 */
export function ContentSection({ className }: { className?: string }) {
  // Same popup-blocker dance as WebsiteDashboardPage: open the tab synchronously on click and only
  // navigate it once the token lands. Awaiting the request first consumes the user-gesture token and
  // the browser blocks the window — which looked exactly like "SSO is broken" when it last shipped.
  // "miniapp" lands on the CMS channel page, not its home — otherwise this button and the one on
  // /website open the same screen and neither tells you which channel you are configuring.
  const openCms = useMutation({ mutationFn: () => websiteApi.ssoLink("miniapp") });

  // Chỉ để biết phòng khám đã được nối Mini App chưa — quyết định có hiện dòng cảnh báo bên
  // dưới hay không. Từ #19433 đây là CHỖ DUY NHẤT trên màn này đọc trạng thái liên kết: thẻ
  // "Ứng dụng cho bệnh nhân" đã gỡ, nên không còn ai chia sẻ khoá truy vấn này nữa.
  const binding = useQuery({
    queryKey: zaloMiniAppQueryKeys.all(),
    queryFn: () => zaloMiniAppApi.get(),
  });
  const notLinked = binding.isSuccess && !binding.data.linked;

  function openCmsTab() {
    // `noopener` cannot be passed to window.open here — it returns null and leaves no handle to
    // navigate. Open bare, then clear `opener` for the same protection.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    openCms.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (tab && !tab.closed) {
          tab.location.replace(url);
        } else {
          window.location.href = url;
        }
      },
      onError: () => tab?.close(),
    });
  }

  return (
    <>
      <Card className={className}>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Nội dung, giao diện và phát hành
            </h2>
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
              Ba thứ này của Mini App nằm ở <b>Addy Sites</b>, mục <b>Zalo Mini App</b> — nút dưới
              mở thẳng vào đó ở <b>tab mới</b>, đăng nhập tự động, Addy HIS vẫn mở để bạn quay lại.
            </p>
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">
              Ở đó bạn đổi màu và logo, sửa bài viết, và bấm <b>Phát hành</b> khi cần đẩy bản mới
              lên Zalo. Sửa nội dung thì bệnh nhân mở lại app là thấy, không phải phát hành lại.
            </p>
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">
              Nội dung soạn một lần và dùng cho cả website lẫn Mini App. Muốn hai kênh khác nhau thì
              mỗi khối có mục <b>Hiển thị</b> để ẩn riêng theo từng kênh.
            </p>

            {notLinked && (
              <p className="mt-3 flex max-w-prose items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/5 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                <span>
                  <b>Mini App chưa được cài đặt.</b> Sang Addy Sites sửa nội dung, đổi màu và
                  logo thì vẫn được, nhưng <b>chưa phát hành lên Zalo được</b>. Việc cài đặt do
                  Addy làm — bạn không phải lấy gì từ Zalo. Liên hệ Addy nếu đã chờ lâu.
                </span>
              </p>
            )}
          </div>
          <Button onClick={openCmsTab} disabled={openCms.isPending}>
            {openCms.isPending ? "Đang mở…" : "Sửa & phát hành Mini App ↗"}
          </Button>
        </CardContent>
      </Card>
      {openCms.isError && (
        <p className="text-sm text-destructive">{apiError(openCms.error).detail}</p>
      )}
    </>
  );
}
